import React, { useState, useEffect } from 'react';
import { 
  FileText, MapPin, Users, Flame, Shield, CheckCircle,
  Heart, Home as HomeIcon, Building, Activity, GraduationCap, Factory,
  Award, Target, Zap, Briefcase, ArrowRight, Phone, Mail,
  MapPinIcon, Building2
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { mockData } from '../mockData';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  FileText, MapPin, Users, Flame, Shield, CheckCircle,
  Heart, Home: HomeIcon, Building, Activity, GraduationCap, Factory,
  Award, Target, Zap, Briefcase
};

const Home = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    contactName: '',
    phone: '',
    email: '',
    address: '',
    businessName: '',
    serviceRequired: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Pre-fill course from URL parameter
    const course = searchParams.get('course');
    if (course) {
      setFormData(prev => ({
        ...prev,
        serviceRequired: course
      }));
    }
    
    // Scroll to contact if requested
    const scrollTo = searchParams.get('scrollTo');
    if (scrollTo === 'contact') {
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceChange = (value) => {
    if (!value) return;
    setFormData(prev => ({
      ...prev,
      serviceRequired: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Reset form
        setFormData({
          contactName: '',
          phone: '',
          email: '',
          address: '',
          businessName: '',
          serviceRequired: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(
        error.response?.data?.detail || 
        'Sorry, there was an error sending your enquiry. Please try again or call us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section id="hero" className="relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={mockData.hero.image} 
            alt="Emergency Management" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/95 via-[#1e3a5f]/90 to-[#1e3a5f]/75"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 mt-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {mockData.hero.title}
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed">
              {mockData.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                {mockData.hero.cta}
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f] px-8 py-6 text-lg font-semibold transition-all duration-300"
              >
                View Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive emergency management and fire safety solutions tailored to your organisation's needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div 
                  key={service.id} 
                  className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group"
                >
                  {service.image ? (
                    <div className="w-full h-48 overflow-hidden bg-gray-100">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-16 flex items-center justify-center mt-6">
                      <div className="w-14 h-14 bg-[#dc2626]/10 rounded-lg flex items-center justify-center group-hover:bg-[#dc2626] transition-colors duration-300">
                        <IconComponent className="text-[#dc2626] group-hover:text-white transition-colors duration-300" size={28} />
                      </div>
                    </div>
                  )}
                  <div className="p-8">
                    <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expert emergency management solutions across all sectors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.industries.map((industry) => {
              const IconComponent = iconMap[industry.icon];
              return (
                <div 
                  key={industry.id} 
                  className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-6 border-2 border-gray-100 hover:border-[#dc2626] transition-all duration-300 transform hover:-translate-y-1 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#1e3a5f] transition-colors duration-300">
                      <IconComponent className="text-[#1e3a5f] group-hover:text-white transition-colors duration-300" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#1e3a5f] mb-2">
                        {industry.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {industry.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-choose-us" className="py-20 bg-[#1e3a5f] text-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#dc2626]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose ShieldComply
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Your trusted partner in emergency management and fire safety compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.whyChooseUs.map((item) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div 
                  key={item.id} 
                  className="text-center group"
                >
                  <div className="w-16 h-16 bg-[#dc2626] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <IconComponent className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Button 
              onClick={scrollToContact}
              size="lg"
              className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              Start Your Safety Journey
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>


      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by organisations across NSW
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockData.testimonials.map((testimonial) => (
              <div 
                key={testimonial.id} 
                className="bg-gray-50 rounded-lg p-8 border-2 border-gray-100 hover:border-[#dc2626] transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="mb-6">
                  <p className="text-gray-600 italic leading-relaxed">
                    "{testimonial.review}"
                  </p>
                </div>
                <div className="border-t-2 border-[#1e3a5f] pt-4">
                  <p className="font-bold text-[#1e3a5f]">
                    {testimonial.name}
                  </p>
                  {testimonial.title && (
                    <p className="text-sm text-gray-600">
                      {testimonial.title}
                    </p>
                  )}
                  {testimonial.company && (
                    <p className="text-sm text-gray-600">
                      {testimonial.company}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Training Gallery Section */}
      <section id="training-gallery" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Real Training in Action
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Trusted by organisations across NSW
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {mockData.trainingGallery.map((item) => (
              <div 
                key={item.id} 
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="relative h-64 overflow-hidden bg-gray-100">
                  <img 
                    src={item.image} 
                    alt={`${item.client} training session`}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1e3a5f] mb-2">
                    {item.client}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {item.industry}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
                Get a Free Consultation
              </h2>
              <p className="text-lg text-gray-600">
                Let's discuss how we can help protect your organisation
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="contactName" className="text-[#1e3a5f] font-semibold">Contact Name *</Label>
                    <Input
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      required
                      className="mt-2 border-gray-300 focus:border-[#dc2626] focus:ring-[#dc2626]"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-[#1e3a5f] font-semibold">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-2 border-gray-300 focus:border-[#dc2626] focus:ring-[#dc2626]"
                      placeholder="0400 000 000"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-[#1e3a5f] font-semibold">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-2 border-gray-300 focus:border-[#dc2626] focus:ring-[#dc2626]"
                      placeholder="john@company.com.au"
                    />
                  </div>

                  <div>
                    <Label htmlFor="businessName" className="text-[#1e3a5f] font-semibold">Business Name *</Label>
                    <Input
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      required
                      className="mt-2 border-gray-300 focus:border-[#dc2626] focus:ring-[#dc2626]"
                      placeholder="Your Company Name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-[#1e3a5f] font-semibold">Address *</Label>
                    <Textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      className="mt-2 border-gray-300 focus:border-[#dc2626] focus:ring-[#dc2626]"
                      placeholder="Street Address, City, State, Postcode"
                    />
                  </div>

                  <div>
                    <Label htmlFor="serviceRequired" className="text-[#1e3a5f] font-semibold">Service Required *</Label>
                    <Select onValueChange={handleServiceChange} value={formData.serviceRequired} required>
                      <SelectTrigger className="mt-2 border-gray-300 focus:border-[#dc2626] focus:ring-[#dc2626]">
                        <SelectValue placeholder="Select a service">{formData.serviceRequired || null}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {formData.serviceRequired && !mockData.serviceOptions.includes(formData.serviceRequired) && (
                          <SelectItem data-testid="prefilled-course-option" value={formData.serviceRequired}>
                            {formData.serviceRequired}
                          </SelectItem>
                        )}
                        {mockData.serviceOptions.map((option, index) => (
                          <SelectItem key={index} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#1e3a5f] font-semibold">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="mt-2 border-gray-300 focus:border-[#dc2626] focus:ring-[#dc2626]"
                      placeholder="Tell us a bit about what you need help with (optional)"
                    />
                  </div>

                  <Button 
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white font-semibold py-6 text-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Submit Request'}
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div className="bg-white rounded-lg shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-[#1e3a5f] mb-6">Contact Information</h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#dc2626]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="text-[#dc2626]" size={24} />
                      </div>
                      <div>
                        <div className="font-semibold text-[#1e3a5f] mb-1">Phone</div>
                        <a href={`tel:${mockData.company.phone}`} className="text-gray-600 hover:text-[#dc2626] transition-colors">
                          {mockData.company.phone}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#dc2626]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="text-[#dc2626]" size={24} />
                      </div>
                      <div>
                        <div className="font-semibold text-[#1e3a5f] mb-1">Email</div>
                        <a href={`mailto:${mockData.company.email}`} className="text-gray-600 hover:text-[#dc2626] transition-colors">
                          {mockData.company.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#dc2626]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPinIcon className="text-[#dc2626]" size={24} />
                      </div>
                      <div>
                        <div className="font-semibold text-[#1e3a5f] mb-1">Service Areas</div>
                        <p className="text-gray-600 font-medium">{mockData.company.address}</p>
                        <p className="text-gray-500 text-sm mt-1">{mockData.company.serviceAreas}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2d5a8f] rounded-lg shadow-lg p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Emergency Response Ready?</h3>
                  <p className="text-gray-200 mb-6 leading-relaxed">
                    Don't wait for an emergency to discover gaps in your safety plans. Get expert guidance today.
                  </p>
                  <div className="flex items-center gap-3">
                    <Shield className="text-[#dc2626]" size={32} />
                    <div>
                      <div className="font-bold text-lg">Get Prepared Today</div>
                      <div className="text-sm text-gray-300">AS 3745 Compliant Solutions</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
